import { Injectable } from '@angular/core';
import { AuthDataService } from '@core/auth/auth.data-service';
import { TokenService } from '@core/auth/token.service';
import { RouterService } from '@core/router/router.service';
import { AccessToken, UserProfile } from '@models';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of, switchMap, tap, withLatestFrom } from 'rxjs';

import * as actions from './auth.actions';

@Injectable()
export class AuthEffects {
  constructor(
    private _actions$: Actions,
    private _authService: AuthDataService,
    private _tokenService: TokenService,
    private _routerService: RouterService,
  ) {}

  public initialize$ = createEffect(() =>
    this._actions$.pipe(
      ofType(actions.initialize),
      mergeMap(() => {
        const token = this._tokenService.accessToken;

        if (token) {
          return [actions.loginSuccess({ token }), actions.fetchProfile()];
        }

        return [];
      }),
    ),
  );

  public login$ = createEffect(() =>
    this._actions$.pipe(
      ofType(actions.login),
      switchMap(({ credentials }) =>
        this._authService.login(credentials).pipe(
          mergeMap((token: AccessToken) => [actions.loginSuccess({ token }), actions.fetchProfile()]),
          catchError(() => of(actions.loginFail())),
        ),
      ),
    ),
  );

  public loginSuccess$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(actions.loginSuccess),
        tap(({ token }) => (this._tokenService.accessToken = token)),
      ),
    { dispatch: false },
  );

  public fetchProfile$ = createEffect(() =>
    this._actions$.pipe(
      ofType(actions.fetchProfile),
      switchMap(() =>
        this._authService.getProfile().pipe(
          map((profile: UserProfile) => actions.fetchProfileSuccess({ profile })),
          catchError(() => of(actions.fetchProfileFail())),
        ),
      ),
    ),
  );

  public fetchProfileSuccess$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(actions.fetchProfileSuccess),
        tap(({ profile }) => (this._tokenService.clientId = profile.clientId)),
        withLatestFrom(this._routerService.selectQueryParam('redirectURL')),
        tap(([_, redirectUrlQueryParam]) =>
          this._routerService.navigateByUrl(redirectUrlQueryParam || '/signed-in-redirect'),
        ),
      ),
    { dispatch: false },
  );

  public logout$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(actions.logout),
        tap(() => this._tokenService.clearTokens()),
      ),
    { dispatch: false },
  );
}
